import React, { ReactNode, RefObject, useMemo } from 'react';
import { RuleSet } from 'styled-components';
import {
  FullScreenButton,
  SliderWrapper,
  LeftArrow,
  Overlay,
  OverlayText,
  RightArrow,
  Slide,
  SlideImage,
  Slider,
  SlideWrapper,
  SlidePicture
} from './styled';
import { TargetedBannerIds, AdriverCustom } from '../../../types';
import {
  SlideRenderType,
  SlideType
} from '../../Ad/ItemPhotosGallery/usePageNumberOnScroll';
import { useGalleryBanner } from '../../../hooks/useTargetedBanner/useGalleryBanner';
import { usePageProps } from '../../../context/PageContext';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';
import { Breakpoints } from '../../../styled/constants';

interface GallerySliderProps {
  photos: SlideType[];
  scrollRef: RefObject<HTMLDivElement | null>;
  sliderInfo?: ReactNode;
  shouldRenderButton?: boolean;
  shouldRenderOverlay?: boolean;
  overlayText?: string;
  sliderStyles?: RuleSet<object>;
  shouldRenderBlurredBackground?: boolean;
  scrollToPrevPhoto: () => void;
  scrollToNextPhoto: () => void;
  onOpenGallery?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  description?: string | ReactNode;
  isBannerActivated?: boolean;
  isNextBanner?: boolean;
  setShouldRenderBanner?: React.Dispatch<React.SetStateAction<boolean>>;
  shouldRenderBanner?: boolean;
  bannerId?: TargetedBannerIds;
  isBannerPosition?: boolean;
  isFullScreen?: boolean;
  adriverCustom?: AdriverCustom;
  showFullScreenButton?: boolean;
}

export const GallerySlider: React.FC<GallerySliderProps> = ({
  photos,
  isFullScreen = false,
  scrollRef,
  sliderInfo,
  shouldRenderOverlay,
  shouldRenderButton,
  overlayText,
  sliderStyles,
  shouldRenderBlurredBackground = true,
  scrollToPrevPhoto,
  scrollToNextPhoto,
  onOpenGallery,
  description = '',
  isBannerActivated,
  isNextBanner,
  setShouldRenderBanner,
  shouldRenderBanner,
  bannerId,
  adriverCustom,
  isBannerPosition = false,
  showFullScreenButton = true
}) => {
  const { mainPageImage } = usePageProps();
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const onLeftArrowClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    scrollToPrevPhoto();
  };

  const onRightArrowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    scrollToNextPhoto();
  };

  const newBannerId = useMemo(
    () => `${bannerId}-${Math.floor(Math.random() * 100000)}`,
    [bannerId]
  );

  useGalleryBanner({
    bannerId: newBannerId,
    isBannerActivated,
    isNextBanner,
    setShouldRenderBanner,
    adriverCustom
  });

  const showOnMobile = useMemo(
    () => (isSmall && isClient) || !isClient,
    [isClient, isSmall]
  );

  const initialPhotosCount = photos.length - 2;

  return (
    <SliderWrapper>
      <Slider
        data-cy="cur-item-gallery"
        ref={scrollRef}
        role="presentation"
        tabIndex={-1}
        $photosCount={photos?.length}
        $styles={sliderStyles}
      >
        {!isBannerPosition && sliderInfo}

        {shouldRenderButton && (
          <>
            {!isBannerPosition && showFullScreenButton && (
              <FullScreenButton onClick={onOpenGallery} />
            )}
            {initialPhotosCount > 1 && (
              <>
                <LeftArrow onClick={onLeftArrowClick} />
                <RightArrow onClick={onRightArrowClick} />
              </>
            )}
          </>
        )}

        {photos.map((slide, idx) => {
          if (slide.type === SlideRenderType.banner) {
            return shouldRenderBanner ? (
              <Slide $isFullScreen={isFullScreen} key={`banner-slide-${idx}`}>
                <div id={newBannerId} />
              </Slide>
            ) : null;
          }

          const { photo, index } = slide;
          const photoF660 = photo?.f660x496;
          const photoFull = photo?.full;
          const thumb = photo?.thumbnail;

          const size = showOnMobile && !isFullScreen ? 'f660x496' : 'full';
          const photoToTake = photo?.[size];
          const isPriority =
            photoToTake === mainPageImage?.[size] && [0, 1].includes(idx);

          return (
            <Slide
              key={`slide-item-${index ?? idx}`}
              data-cy="cur-item-gallery__slide"
              role="button"
              tabIndex={-1}
              onClick={onOpenGallery}
              onKeyPress={
                onOpenGallery as unknown as React.KeyboardEventHandler<HTMLDivElement>
              }
              $withBlur={shouldRenderBlurredBackground}
            >
              <SlidePicture>
                {isFullScreen && <source srcSet={photoFull} />}
                <source
                  media={`(min-width: ${Breakpoints.tablet}px)`}
                  srcSet={photoFull}
                />
                <source
                  media={`(max-width: ${Breakpoints.tablet - 1}px)`}
                  srcSet={photoF660}
                />

                <SlideImage
                  src={photoToTake}
                  loading={isPriority ? 'eager' : 'lazy'}
                  fetchPriority={isPriority ? 'high' : 'low'}
                  width={660}
                  height={496}
                  alt={`${description ? `${description}, ` : ''}photo ${(index || 0) + 1} from ${photos?.length}`}
                />
              </SlidePicture>

              {shouldRenderBlurredBackground && (
                <SlideWrapper $photo={thumb} aria-hidden />
              )}
            </Slide>
          );
        })}

        {shouldRenderOverlay && overlayText && (
          <Overlay data-cy="cur-item-gallery__overlay">
            <OverlayText tag="header_bold_new">{overlayText}</OverlayText>
          </Overlay>
        )}
      </Slider>
    </SliderWrapper>
  );
};
